import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueries, useMutation } from '@tanstack/react-query';
import {
  addMonths,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  endOfMonth,
  subMonths,
  parse,
  isValid,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isBefore,
  startOfDay
} from 'date-fns';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';

import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import {
  getBookingLinkInfo,
  getAvailableSlots,
  bookAppointment,
} from '@/lib/api/publicBooking';
import type { AppointmentAvailableSlot } from '@/lib/api/appointments';
import {
  scheduleDurationOptions,
  weekDayLabels,
  formatScheduleSlotLabel,
} from '@/utils/appointments/schedule-dialog-utils';
import { isDateInTimeOff } from '@/utils/appointments';
import {
  calendarDayFromYmd,
  getClientCalendarDayKey,
  getClientDisplayDayKey,
  isPublicBookingCalendarDaySelectable,
  isSameClientCalendarDay,
  resolveScheduleLookupDatesForClientDay,
  mergeAvailableSlotsByUtc,
  filterSlotsForClientCalendarDay,
} from '@/utils/appointments/public-booking-calendar';
import { ScheduleCalendarDisabledDay } from '@/components/appointments/ScheduleCalendarDisabledDay';
import {
  schedulePanelGroupSx,
  schedulePanelInputSx,
  schedulePanelLabelSx,
  schedulePanelSectionSx,
  schedulePanelSelectSx,
  scheduleCalendarPanelSx,
  scheduleCenterTitleSx,
  scheduleCenterSubtitleSx,
  publicBookingPastDayTooltip,
  publicBookingUnavailableDayTooltip,
} from '@/components/appointments/scheduleDialogStyles';

function isPastScheduleDay(date: Date): boolean {
  return isBefore(date, startOfDay(new Date()));
}

const PublicBookingPage: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>();

  const [scheduleMonth, setScheduleMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AppointmentAvailableSlot | null>(null);
  
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [appointmentTitle, setAppointmentTitle] = useState('');
  const [isBooked, setIsBooked] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState<number>(30);
  const [meetingType, setMeetingType] = useState<'online' | 'in_person'>('online');

  const {
    data: linkInfoResponse,
    isLoading: isLoadingLinkInfo,
    isError: isLinkError,
  } = useQuery({
    queryKey: ['bookingLinkInfo', linkId],
    queryFn: () => getBookingLinkInfo(linkId!),
    enabled: !!linkId,
    retry: false,
  });

  const linkInfo = linkInfoResponse?.data;
  const timeOffEntries = useMemo(() => {
    const raw = linkInfo?.timeOff;
    return Array.isArray(raw) ? raw : [];
  }, [linkInfo?.timeOff]);
  const userTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const scheduleTimezoneHint = linkInfo?.nutritionist?.timezone?.trim() || userTimezone;

  // Pre-fill client info if available in the link
  useEffect(() => {
    if (linkInfo?.client) {
      if (linkInfo.client.name) setClientName(linkInfo.client.name);
      if (linkInfo.client.email) setClientEmail(linkInfo.client.email);
    }
  }, [linkInfo?.client]);

  useEffect(() => {
    if (!selectedDate || timeOffEntries.length === 0) return;
    if (isDateInTimeOff(selectedDate, timeOffEntries)) {
      setSelectedDate(null);
      setSelectedSlot(null);
    }
  }, [selectedDate, timeOffEntries]);

  const scheduleDays = linkInfo?.schedule?.days ?? [];
  const clientDateKey = selectedDate ? getClientCalendarDayKey(selectedDate) : null;
  const lookupDates = useMemo(
    () =>
      selectedDate && scheduleTimezoneHint
        ? resolveScheduleLookupDatesForClientDay(
            selectedDate,
            scheduleTimezoneHint,
            userTimezone,
            scheduleDays,
          )
        : [],
    [selectedDate, scheduleTimezoneHint, userTimezone, scheduleDays],
  );
  const lookupDate = lookupDates[0] ?? null;

  const slotQueries = useQueries({
    queries: lookupDates.map((date) => ({
      queryKey: ['bookingAvailableSlots', linkId, date, clientDateKey, durationMinutes, userTimezone],
      queryFn: () => getAvailableSlots(linkId!, date, durationMinutes, userTimezone),
      enabled: Boolean(linkId && date),
    })),
  });

  const isLoadingSlots = slotQueries.some((query) => query.isLoading);

  const slots = useMemo(() => {
    const merged = mergeAvailableSlotsByUtc(
      slotQueries.flatMap((query) => query.data?.availableSlots ?? []),
    );
    if (!selectedDate) return merged;
    return filterSlotsForClientCalendarDay(merged, selectedDate, userTimezone);
  }, [slotQueries, selectedDate, userTimezone]);


  const bookMutation = useMutation({
    mutationFn: async () => {
      if (!linkId || !selectedSlot) throw new Error('Missing booking info');

      const title = appointmentTitle.trim() || 'Online Consultation';
      const appointmentType = meetingType === 'in_person' ? 'offline' : 'online';
      const clientFields = {
        clientName: clientName.trim() || undefined,
        clientEmail: clientEmail.trim() || undefined,
        additionalAttendees: clientEmail.trim() ? [clientEmail.trim()] : undefined,
      };

      if (selectedSlot.startTimeUtc && selectedSlot.endTimeUtc) {
        return bookAppointment(linkId, {
          title,
          startTime: selectedSlot.startTimeUtc,
          endTime: selectedSlot.endTimeUtc,
          appointmentType,
          ...clientFields,
        });
      }

      const parsedTime = parse(selectedSlot.startTime, 'HH:mm', new Date());
      if (!isValid(parsedTime)) throw new Error('Invalid time slot selected');

      const endTimeStr =
        selectedSlot.endTime ||
        format(new Date(parsedTime.getTime() + durationMinutes * 60000), 'HH:mm');

      return bookAppointment(linkId, {
        title,
        date: clientDateKey ?? lookupDate ?? undefined,
        startTime: selectedSlot.startTime,
        endTime: endTimeStr,
        appointmentType,
        timezone: userTimezone,
        ...clientFields,
      });
    },
    onSuccess: () => {
      setIsBooked(true);
      showSuccessToast('Booking Confirmed', 'Your appointment has been successfully scheduled.');
    },
    onError: (error) => {
      showErrorToast(error, 'Failed to book appointment');
    },
  });

  const handleBook = () => {
    if (!selectedDate || !selectedSlot) {
      showErrorToast(new Error('Please select a date and time'), 'Incomplete selection');
      return;
    }
    if (!appointmentTitle.trim()) {
      showErrorToast(new Error('Please enter an appointment name'), 'Incomplete details');
      return;
    }
    if (!linkInfo?.client && (!clientName.trim() || !clientEmail.trim())) {
      showErrorToast(new Error('Please provide your name and email'), 'Incomplete details');
      return;
    }
    bookMutation.mutate();
  };

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(scheduleMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(scheduleMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [scheduleMonth]);

  const isDayAvailable = (day: Date) =>
    isPublicBookingCalendarDaySelectable(day, {
      timeOff: timeOffEntries,
      clientTimezone: userTimezone,
      scheduleTimezone: scheduleTimezoneHint,
      scheduleDays,
    });

  const handleDatePick = (day: Date) => {
    const isOutsideCurrentMonth = !isSameMonth(day, scheduleMonth);
    if (isOutsideCurrentMonth) {
      setScheduleMonth(startOfMonth(day));
    }

    if (isPastScheduleDay(day)) return;
    if (!isDayAvailable(day)) return;

    setSelectedDate(day);
    setSelectedSlot(null);
  };

  if (isLoadingLinkInfo) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F2F7FA' }}>
        <CircularProgress size={32} sx={{ color: '#2FA8D5' }} />
      </Box>
    );
  }

  if (isLinkError || !linkInfo) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F2F7FA' }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: '16px', maxWidth: 400, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '1.25rem', color: '#1D2939' }}>
            Booking link invalid or expired
          </Typography>
          <Typography sx={{ mt: 1, fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', color: '#667085' }}>
            This link is either no longer valid, has already been used, or an error occurred.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (isBooked) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F2F7FA' }}>
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, borderRadius: '20px', maxWidth: 500, width: '100%', textAlign: 'center', boxShadow: '0 30px 70px rgba(16, 24, 40, 0.1)' }}>
          <Box
            sx={{
              width: 68,
              height: 68,
              mx: 'auto',
              borderRadius: '999px',
              bgcolor: '#DDFBE8',
              border: '1px solid #A6F4C5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#18A565',
            }}
          >
            <CheckRoundedIcon sx={{ fontSize: 34 }} />
          </Box>

          <Typography
            sx={{
              mt: 2,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.4rem' },
              color: '#1D2939',
              lineHeight: 1.2,
            }}
          >
            Appointment is created with {linkInfo.nutritionist.name || 'Nutritionist'}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              fontWeight: 600,
              color: '#2FA8D5',
            }}
          >
            for {format(selectedDate!, 'MMMM d, yyyy')} at{' '}
            {formatScheduleSlotLabel(selectedSlot!.startTime, durationMinutes)}.
          </Typography>

          {clientEmail && !clientEmail.includes('@redacted.local') && (
            <Typography sx={{ mt: 3, fontFamily: 'Inter, sans-serif', fontSize: '0.875rem', color: '#98A2B3' }}>
              A confirmation email will be sent to {clientEmail}.
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F2F7FA', display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 0, sm: 2 } }}>
      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '340px minmax(0, 1fr) 220px' },
          width: 'min(1180px, calc(100vw - 32px))',
          height: { xs: 'auto', md: 'min(760px, calc(100vh - 40px))' },
          minHeight: { xs: '100vh', md: 0 },
          borderRadius: { xs: 0, md: '22px' },
          overflow: 'hidden',
          bgcolor: '#FFFFFF',
          boxShadow: '0 20px 60px rgba(16, 24, 40, 0.1)',
        }}
      >
        <Box
          sx={{
            px: 2.2,
            py: 2.3,
            bgcolor: '#1184B1',
            background: 'linear-gradient(180deg, #0F8AB5 0%, #1AA3D0 55%, #2AA9D7 100%)',
            color: '#fff',
            overflowY: 'auto',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '12px',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'rgba(226, 244, 252, 0.95)',
            }}
          >
            Booking Link
          </Typography>
          <Typography
            sx={{
              mt: 0.6,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '1.6rem',
              lineHeight: 1.1,
              color: '#fff',
            }}
          >
            Schedule Session
          </Typography>

          <Box sx={schedulePanelSectionSx}>
            <Typography sx={schedulePanelLabelSx}>Session details</Typography>
            <Box sx={schedulePanelGroupSx}>
              <Box>
                <Typography sx={schedulePanelLabelSx}>Appointment Name</Typography>
                <TextField
                  value={appointmentTitle}
                  onChange={(event) => setAppointmentTitle(event.target.value)}
                  placeholder="Initial Consultation"
                  fullWidth
                  size="small"
                  sx={{ mt: 0.75, ...schedulePanelInputSx }}
                />
              </Box>
              
              <Box>
                <Typography sx={schedulePanelLabelSx}>Duration</Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={durationMinutes}
                  onChange={(event) => {
                    setDurationMinutes(Number(event.target.value));
                    setSelectedSlot(null);
                  }}
                  SelectProps={{
                    IconComponent: ExpandMoreRoundedIcon,
                  }}
                  sx={{ mt: 0.75, ...schedulePanelSelectSx }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTimeRoundedIcon sx={{ color: '#d9eef7', fontSize: 16 }} />
                      </InputAdornment>
                    ),
                  }}
                >
                  {scheduleDurationOptions.map((minutes) => (
                    <MenuItem key={minutes} value={minutes}>
                      {minutes} minutes
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box>
                <Typography sx={schedulePanelLabelSx}>Meeting Type</Typography>
                <Box
                  sx={{
                    mt: 0.75,
                    p: 0.28,
                    borderRadius: '14px',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 0.32,
                  }}
                >
                  <Button
                    disableElevation
                    onClick={() => setMeetingType('online')}
                    sx={{
                      minHeight: 34,
                      borderRadius: '13px',
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      bgcolor: meetingType === 'online' ? '#fff' : 'transparent',
                      color: meetingType === 'online' ? '#0F3042' : 'rgba(226, 244, 252, 0.9)',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: meetingType === 'online' ? '#fff' : 'rgba(255,255,255,0.08)',
                      },
                    }}
                  >
                    Online
                  </Button>
                  <Button
                    disableElevation
                    onClick={() => setMeetingType('in_person')}
                    sx={{
                      minHeight: 34,
                      borderRadius: '13px',
                      textTransform: 'none',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      bgcolor: meetingType === 'in_person' ? '#fff' : 'transparent',
                      color: meetingType === 'in_person' ? '#0F3042' : 'rgba(226, 244, 252, 0.9)',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: meetingType === 'in_person' ? '#fff' : 'rgba(255,255,255,0.08)',
                      },
                    }}
                  >
                    In-Person
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>

          {!linkInfo.client && (
            <Box
              sx={{
                mt: 2.25,
                pt: 1.75,
                borderTop: '1px solid rgba(181, 235, 255, 0.25)',
              }}
            >
              <Typography sx={schedulePanelLabelSx}>Your Contact Info</Typography>
              <Box sx={schedulePanelGroupSx}>
                <Box>
                  <Typography sx={schedulePanelLabelSx}>Full Name</Typography>
                  <TextField
                    value={clientName}
                    onChange={(event) => setClientName(event.target.value)}
                    placeholder="Jane Doe"
                    fullWidth
                    size="small"
                    sx={{ mt: 0.75, ...schedulePanelInputSx }}
                  />
                </Box>
                <Box>
                  <Typography sx={schedulePanelLabelSx}>Email Address</Typography>
                  <TextField
                    value={clientEmail}
                    onChange={(event) => setClientEmail(event.target.value)}
                    placeholder="jane@example.com"
                    fullWidth
                    size="small"
                    sx={{ mt: 0.75, ...schedulePanelInputSx }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              mt: 2.25,
              pt: 1.75,
              borderTop: '1px solid rgba(181, 235, 255, 0.25)',
            }}
          >
            <Typography sx={schedulePanelLabelSx}>Host</Typography>
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {linkInfo.nutritionist.profileImageUrl ? (
                <Box
                  component="img"
                  src={linkInfo.nutritionist.profileImageUrl}
                  alt={linkInfo.nutritionist.name || 'Nutritionist'}
                  sx={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  {linkInfo.nutritionist.name?.charAt(0) || 'N'}
                </Box>
              )}
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    color: '#fff',
                  }}
                >
                  {linkInfo.nutritionist.name || 'Nutritionist'}
                </Typography>
                {linkInfo.nutritionist.email ? (
                  <Typography
                    sx={{
                      mt: 0.1,
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '13px',
                      color: 'rgba(226, 244, 252, 0.78)',
                    }}
                  >
                    {linkInfo.nutritionist.email}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </Box>

          {(linkInfo.client || clientName.trim() || clientEmail.trim()) ? (
            <>
              <Typography
                sx={{
                  mt: 2,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '12px',
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: 'rgba(226, 244, 252, 0.95)',
                }}
              >
                Client
              </Typography>
              <Typography
                sx={{
                  mt: 0.52,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  color: '#fff',
                }}
              >
                {linkInfo.client?.name || clientName.trim() || 'Client'}
              </Typography>
              {((linkInfo.client?.email || clientEmail.trim()) && !(linkInfo.client?.email || clientEmail.trim()).includes('@redacted.local')) ? (
                <Typography
                  sx={{
                    mt: 0.12,
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    color: 'rgba(226, 244, 252, 0.78)',
                  }}
                >
                  {linkInfo.client?.email || clientEmail.trim()}
                </Typography>
              ) : null}
            </>
          ) : null}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateRows: 'auto minmax(0, 1fr) 80px', minWidth: 0, height: '100%', minHeight: 0 }}>
          <Box sx={{ position: 'relative', zIndex: 4, px: 2.5, py: 2, borderBottom: '1px solid #EAF0F5', bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '10px',
                  bgcolor: '#E9F5FB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <PersonRoundedIcon sx={{ fontSize: 18, color: '#2FA8D5' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={scheduleCenterTitleSx}>
                  Complete Your Booking
                </Typography>
                <Typography sx={scheduleCenterSubtitleSx}>
                  Fill in your details and choose a time
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              px: 2.5,
              py: 2,
              minHeight: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
            }}
          >
            <Box>
              <Typography sx={scheduleCenterTitleSx}>Select a Date</Typography>
              <Typography sx={scheduleCenterSubtitleSx}>Choose any available weekday below</Typography>
            </Box>
            <Box sx={scheduleCalendarPanelSx}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '40px minmax(0, 1fr) 40px', alignItems: 'center' }}>
                <IconButton
                  onClick={() => setScheduleMonth((value) => subMonths(value, 1))}
                  sx={{ width: 30, height: 30, color: '#98A2B3' }}
                >
                  <NavigateBeforeRoundedIcon />
                </IconButton>
                <Typography
                  sx={{
                    textAlign: 'center',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    color: '#1D2939',
                  }}
                >
                  {format(scheduleMonth, 'MMMM yyyy')}
                </Typography>
                <IconButton
                  onClick={() => setScheduleMonth((value) => addMonths(value, 1))}
                  sx={{ width: 30, height: 30, color: '#98A2B3' }}
                >
                  <NavigateNextRoundedIcon />
                </IconButton>
              </Box>

              <Box sx={{ mt: 1.8, display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', columnGap: 0.35, rowGap: 1.25 }}>
                {weekDayLabels.map((label) => (
                  <Typography
                    key={label}
                    sx={{
                      textAlign: 'center',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      letterSpacing: '0.04em',
                      color: '#C2CAD7',
                    }}
                  >
                    {label}
                  </Typography>
                ))}

                {calendarDays.map((day) => {
                  const isCurrentMonth = isSameMonth(day, scheduleMonth);
                  const isOutsideCurrentMonth = !isCurrentMonth;
                  const isPastDay = isPastScheduleDay(day);
                  const isUnavailableDay = !isOutsideCurrentMonth && !isPastDay && !isDayAvailable(day);
                  const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;

                  if (isPastDay) {
                    return (
                      <ScheduleCalendarDisabledDay
                        key={day.toISOString()}
                        day={day}
                        tooltip={publicBookingPastDayTooltip}
                      />
                    );
                  }

                  if (isUnavailableDay) {
                    return (
                      <ScheduleCalendarDisabledDay
                        key={day.toISOString()}
                        day={day}
                        tooltip={publicBookingUnavailableDayTooltip}
                      />
                    );
                  }

                  return (
                    <Button
                      key={day.toISOString()}
                      disableElevation
                      onClick={() => handleDatePick(day)}
                      sx={{
                        minHeight: 40,
                        borderRadius: '10px',
                        p: 0,
                        minWidth: 0,
                        textTransform: 'none',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: isSelected ? 700 : 600,
                        fontSize: '1rem',
                        color: isOutsideCurrentMonth
                          ? '#D3DBE6'
                          : isSelected
                            ? '#fff'
                            : '#1D2939',
                        bgcolor: isOutsideCurrentMonth
                          ? 'transparent'
                          : isSelected
                            ? '#2FA8D5'
                            : 'transparent',
                        '&:hover': {
                          bgcolor: isOutsideCurrentMonth
                            ? 'transparent'
                            : isSelected
                              ? '#2FA8D5'
                              : '#F2F7FA',
                        },
                      }}
                    >
                      {format(day, 'd')}
                    </Button>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box sx={{ px: 2.5, py: 1.1, borderTop: '1px solid #EEF2F6', bgcolor: '#fff', display: 'flex', alignItems: 'center' }}>
            <Button
              disableElevation
              disabled={
                !selectedDate ||
                !selectedSlot ||
                !appointmentTitle.trim() ||
                (!linkInfo?.client && (!clientName.trim() || !clientEmail.trim())) ||
                bookMutation.isPending
              }
              onClick={() => handleBook()}
              sx={{
                width: '100%',
                minHeight: 50,
                borderRadius: '14px',
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '1.05rem',
                bgcolor:
                  selectedDate &&
                  selectedSlot &&
                  appointmentTitle.trim() &&
                  (linkInfo?.client || (clientName.trim() && clientEmail.trim()))
                    ? '#2FA8D5'
                    : '#EEF2F6',
                color:
                  selectedDate &&
                  selectedSlot &&
                  appointmentTitle.trim() &&
                  (linkInfo?.client || (clientName.trim() && clientEmail.trim()))
                    ? '#fff'
                    : '#98A2B3',
                '&:hover': {
                  bgcolor:
                    selectedDate &&
                    selectedSlot &&
                    appointmentTitle.trim() &&
                    (linkInfo?.client || (clientName.trim() && clientEmail.trim()))
                      ? '#249CCB'
                      : '#EEF2F6',
                },
              }}
            >
              {bookMutation.isPending
                ? 'Confirming…'
                : !appointmentTitle.trim()
                  ? 'Enter appointment name'
                  : (!linkInfo?.client && (!clientName.trim() || !clientEmail.trim()))
                    ? 'Enter your details'
                    : !selectedDate
                      ? 'Select a date'
                      : selectedSlot
                        ? 'Confirm Booking'
                        : 'Pick a time slot'}
            </Button>
          </Box>
        </Box>

        <Box sx={{ borderLeft: '1px solid #EAF0F5', bgcolor: '#FBFDFF', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box
            sx={{
              px: 2,
              pt: 2.2,
              pb: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {selectedDate ? (
                <>
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: '#1D2939',
                    }}
                  >
                    {format(selectedDate, 'EEE')} · {format(selectedDate, 'd MMM')}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.3,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '0.78rem',
                      color: '#98A2B3',
                    }}
                  >
                    {isLoadingSlots
                      ? 'Loading available times…'
                      : `${slots.length} slot${slots.length === 1 ? '' : 's'} available · ${durationMinutes} min each`}
                  </Typography>
                </>
              ) : (
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: '#1D2939',
                  }}
                >
                  Available times
                </Typography>
              )}
            </Box>
          </Box>
          {selectedDate ? (
            <>
              <Box sx={{ px: 1.5, pb: 1.5, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                {isLoadingSlots ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={28} sx={{ color: '#2FA8D5' }} />
                  </Box>
                ) : slots.length === 0 ? (
                  <Typography
                    sx={{
                      py: 2,
                      textAlign: 'center',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '0.86rem',
                      color: '#98A2B3',
                    }}
                  >
                    No available times on this day
                  </Typography>
                ) : (
                  <Box sx={{ display: 'grid', gap: 0.75 }}>
                    {slots.map((slot) => {
                      const isSelected = selectedSlot?.startTime === slot.startTime;
                      return (
                        <Button
                          key={`${slot.startTime}-${slot.startTimeUtc}`}
                          onClick={() => {
                            setSelectedSlot(slot);
                          }}
                          sx={{
                            minHeight: 40,
                            borderRadius: '14px',
                            textTransform: 'none',
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 700,
                            fontSize: '0.92rem',
                            border: '1px solid #D7E1EB',
                            color: isSelected ? '#fff' : '#475467',
                            bgcolor: isSelected ? '#2FA8D5' : '#fff',
                            '&:hover': {
                              bgcolor: isSelected ? '#249CCB' : '#F7FAFD',
                            },
                          }}
                        >
                          {formatScheduleSlotLabel(slot.startTime, durationMinutes)}
                        </Button>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ flex: 1, px: 2, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    mx: 'auto',
                    borderRadius: '16px',
                    bgcolor: '#EAF0F5',
                    color: '#9DAAB8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AccessTimeRoundedIcon />
                </Box>
                <Typography
                  sx={{
                    mt: 1.2,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.86rem',
                    lineHeight: 1.35,
                    color: '#98A2B3',
                  }}
                >
                  Pick a date to see available times
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PublicBookingPage;
